import { useCallback, useEffect, useReducer, useRef } from 'react';

import type {
  AsyncOp,
  AsyncResolverOptions,
  AsyncResolverState,
  RetryPolicy,
} from './types';

/**
 * Error a source may throw to hand the primitive an explicit `Retry-After`
 * hint (in milliseconds). When present and the retry policy honors it, the
 * primitive waits exactly this long instead of computing a backoff delay.
 *
 * The primitive stays source-agnostic: it reads {@link retryAfterMs} off any
 * thrown value, so an adapter that already parses a `429` `Retry-After` header
 * can throw this (or any error carrying a numeric `retryAfterMs`) without the
 * primitive knowing anything about HTTP.
 */
export class RetryAfterError extends Error {
  readonly retryAfterMs: number;

  constructor(message: string, retryAfterMs: number) {
    super(message);
    this.name = 'RetryAfterError';
    this.retryAfterMs = retryAfterMs;
  }
}

const DEFAULT_RETRY: RetryPolicy = {
  baseDelayMs: 300,
  honorRetryAfter: true,
  maxAttempts: 1,
};

/**
 * Upper bound on any retry delay (ms). A Retry-After hint or a computed backoff
 * is clamped to this so a hostile or buggy source cannot park a retry for
 * minutes. 60s is long enough for a real throttle window, short enough to keep
 * the UI recoverable.
 */
const MAX_RETRY_DELAY_MS = 60_000;

/** Fallback base delay (ms) when a policy supplies a non-finite `baseDelayMs`. */
const FALLBACK_BASE_DELAY_MS = 300;

/**
 * Cap on resolver-local cache entries. The cache is per hook instance and keyed
 * per distinct context, so an unbounded map would grow with every distinct
 * key over a component's lifetime. On overflow the oldest entry is evicted
 * (Map keeps insertion order), a simple FIFO that bounds memory.
 */
export const MAX_CACHE_ENTRIES = 500;
let unstableCacheKeySequence = 0;

/** Unique sentinel for "no cache entry", distinct from any real result value. */
const MISS = Symbol('async-resolver-cache-miss');

/**
 * Read a `Retry-After` hint (ms) off an unknown thrown value, if it carries
 * one. The primitive stays source-agnostic: any error with a numeric
 * `retryAfterMs` property (not only {@link RetryAfterError}) supplies the hint.
 *
 * Only a finite, non-negative number counts. A source that hands over `NaN`,
 * `Infinity` or a negative value yields `undefined`, so the retry falls back to
 * backoff rather than passing junk straight into `setTimeout`.
 */
function readRetryAfterMs(error: unknown): number | undefined {
  let raw: unknown;
  if (error instanceof RetryAfterError) {
    raw = error.retryAfterMs;
  } else if (typeof error === 'object' && error !== null) {
    raw = Reflect.get(error, 'retryAfterMs');
  } else {
    return undefined;
  }
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0
    ? raw
    : undefined;
}

/**
 * Delay before retry `attempt` (1-based): the Retry-After hint wins when
 * present and honored, otherwise exponential backoff derived from
 * `baseDelayMs`. The result is always clamped to `[0, MAX_RETRY_DELAY_MS]` so a
 * junk hint or a junk `baseDelayMs` can never reach `setTimeout` as-is.
 */
function computeDelayMs(
  attempt: number,
  policy: RetryPolicy,
  retryAfterMs: number | undefined,
): number {
  if (policy.honorRetryAfter && retryAfterMs !== undefined) {
    return clampDelay(retryAfterMs);
  }
  // A consumer-supplied `baseDelayMs` is not validated upstream; fall back when
  // it is non-finite OR non-positive. A finite-negative or zero base would pass
  // `Number.isFinite` yet drive the exponential to <= 0, which `clampDelay`
  // floors to 0 — a delay-less retry loop that hammers a failing source. Treat
  // any non-positive base as junk and use the fallback so every retry waits.
  const base =
    Number.isFinite(policy.baseDelayMs) && policy.baseDelayMs > 0
      ? policy.baseDelayMs
      : FALLBACK_BASE_DELAY_MS;
  const exponential = base * 2 ** (attempt - 1);
  // Additive jitter on top of the exponential base: retries never fall below
  // `baseDelayMs` yet spread out to avoid a thundering herd. (Not "full"
  // jitter, which would replace the base with a random draw in [0, base].)
  const entropy = new Uint32Array(1);
  globalThis.crypto.getRandomValues(entropy);
  const [sample = 0] = entropy;
  const jitter = (sample / 2 ** 32) * base;
  return clampDelay(exponential + jitter);
}

/** Bound a delay to a finite, non-negative value no larger than the cap. */
function clampDelay(delayMs: number): number {
  if (!Number.isFinite(delayMs)) return FALLBACK_BASE_DELAY_MS;
  return Math.min(Math.max(0, delayMs), MAX_RETRY_DELAY_MS);
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

/**
 * Insert into the resolver-local cache with a FIFO size cap. A Map preserves
 * insertion order, so the first key is the oldest; once the cap is exceeded it
 * is evicted. Re-setting an existing key refreshes its value but does not move
 * it to the end (Map keeps the original position), which is acceptable for a
 * simple FIFO bound.
 */
export function setCapped<TResult>(
  cache: Map<string, TResult>,
  key: string,
  value: TResult,
): void {
  cache.set(key, value);
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/**
 * Stable dependency key for a context. When the consumer supplies `cacheKey`
 * that key drives both caching and change-detection. Otherwise a structural
 * `JSON.stringify` gives a deterministic key so a fresh `ctx` object with equal
 * contents does not spuriously re-fetch. This key is the effect dependency, so
 * the effect only runs when the context actually changes — not on every render.
 */
function deriveKey<TCtx>(
  ctx: TCtx,
  cacheKey: ((ctx: TCtx) => string) | undefined,
): string {
  if (cacheKey) return cacheKey(ctx);
  try {
    // Wrapping in an array guarantees a string: `JSON.stringify([undefined])`
    // is `"[null]"`, never `undefined`, so the key is always well-defined.
    return JSON.stringify([ctx]);
  } catch {
    // Non-serializable ctx (cycles, functions): fall back to a per-render key
    // so the resolver still re-evaluates rather than getting stuck. That
    // fallback re-fetches on every render (a silent storm), so warn in dev to
    // make it detectable. Dev-only console.warn is the one console call this
    // primitive allows: it must not spam production logs, hence the NODE_ENV
    // guard, and it points the consumer at the fix (a stable `cacheKey`).
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[react-async-utils] useAsyncResolver received a non-serializable ' +
          'context with no `cacheKey`; falling back to a per-render key, which ' +
          're-fetches on every render. Supply a stable `cacheKey` to fix this.',
      );
    }
    return String(++unstableCacheKeySequence);
  }
}

type State<TResult> = AsyncResolverState<TResult>;

type Action<TResult> =
  | { type: 'idle' }
  | { type: 'loading' }
  | { data: TResult; type: 'success' }
  | { error: Error; retry: () => void; type: 'error' };

function assertNeverAction(action: never): never {
  throw new TypeError(`Unhandled async resolver action: ${String(action)}`);
}

/**
 * The single async-fetch lifecycle primitive shared across every picker source
 * and the techradar live-fetch. It owns loading, error, retry (Retry-After
 * before backoff), cancellation, cascade-gating, stale-response suppression and
 * a resolver-local cache. It knows nothing of pickers, OptionSource or Azure.
 *
 * Four behavioural invariants:
 * 1. Cascade-gating: while `isReady(ctx)` is `false`, status stays `idle` and
 *    no call departs.
 * 2. Herfetch-on-change: when the context key changes, the in-flight call is
 *    aborted and a new one starts.
 * 3. Stale-suppression: a late answer from a superseded call is discarded.
 * 4. Retry-After precedence: a Retry-After hint wins over the additive-jitter
 *    exponential backoff.
 *
 * Non-goals: no global/cross-component cache, no
 * infinite-query abstraction, no cross-field request dedup, no
 * stale-while-revalidate. The cache here is local to this hook instance.
 */
export function useAsyncResolver<TCtx, TResult>(
  op: AsyncOp<TCtx, TResult>,
  ctx: TCtx,
  opts?: AsyncResolverOptions<TCtx>,
): AsyncResolverState<TResult> {
  const initialState: State<TResult> = { status: 'idle' };
  const [state, dispatch] = useReducer(
    (prev: State<TResult>, action: Action<TResult>): State<TResult> => {
      switch (action.type) {
        case 'idle':
          return prev.status === 'idle' ? prev : { status: 'idle' };
        case 'loading':
          return prev.status === 'loading' ? prev : { status: 'loading' };
        case 'success':
          return { data: action.data, status: 'success' };
        case 'error':
          return { error: action.error, retry: action.retry, status: 'error' };
        default:
          return assertNeverAction(action);
      }
    },
    initialState,
  );

  // Latest render's inputs, read by the run loop so the effect can depend on a
  // stable key rather than the identity of `op` / `opts` / `ctx`.
  const opRef = useRef(op);
  opRef.current = op;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  // Monotonic id of the current logical request; a resolution whose id no
  // longer matches is stale and gets dropped (invariant 3).
  const requestIdRef = useRef(0);
  const controllerRef = useRef<AbortController | undefined>(undefined);
  const mountedRef = useRef(true);
  // Resolver-local cache: equal key = cache hit (non-goal: no shared store).
  const cacheRef = useRef<Map<string, TResult>>(new Map());
  // Pending debounce timer, if `debounceMs` is set. Cleared on every new
  // context change and on unmount so a stale timer never fires.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
      if (debounceTimerRef.current !== undefined) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = undefined;
      }
    };
  }, []);

  const run = useCallback(() => {
    const currentCtx = ctxRef.current;
    const currentOpts = optsRef.current;
    const isReady = currentOpts?.isReady;

    // Invariant 1: cascade-gating. Stay idle, never depart a call.
    if (isReady && !isReady(currentCtx)) {
      controllerRef.current?.abort();
      requestIdRef.current += 1;
      dispatch({ type: 'idle' });
      return;
    }

    const key = currentOpts?.cacheKey?.(currentCtx);
    // `?? MISS` folds a stored nullish value into a miss too. The resolver never
    // caches nullish (only a resolved `data`), so this only fires on a genuinely
    // absent key, never on a real cached result that happens to be null.
    const cached = key === undefined ? MISS : cacheRef.current.get(key) ?? MISS;
    if (cached !== MISS) {
      controllerRef.current?.abort();
      requestIdRef.current += 1;
      dispatch({ data: cached, type: 'success' });
      return;
    }

    // Invariant 2: abort the previous, now-superseded call before a new one.
    controllerRef.current?.abort();
    const requestId = (requestIdRef.current += 1);
    const controller = new AbortController();
    controllerRef.current = controller;

    const policy = currentOpts?.retry ?? DEFAULT_RETRY;

    const isStale = () =>
      !mountedRef.current ||
      requestId !== requestIdRef.current ||
      controller.signal.aborted;

    const attempt = async (attemptNo: number): Promise<void> => {
      try {
        const data = await opRef.current(currentCtx, controller.signal);
        if (isStale()) return;
        if (key !== undefined) setCapped(cacheRef.current, key, data);
        dispatch({ data, type: 'success' });
      } catch (error_) {
        if (isStale()) return;
        if (error_ instanceof Error && error_.name === 'AbortError') return;

        if (attemptNo < policy.maxAttempts) {
          const delayMs = computeDelayMs(
            attemptNo,
            policy,
            readRetryAfterMs(error_),
          );
          await new Promise<void>(resolve => {
            setTimeout(resolve, delayMs);
          });
          if (isStale()) return;
          await attempt(attemptNo + 1);
          return;
        }

        dispatch({
          error: toError(error_),
          retry: run,
          type: 'error',
        });
      }
    };

    dispatch({ type: 'loading' });
    void attempt(1);
    // Empty dep list is intentional: every input (`op`, `opts`, `ctx`) is read
    // through a ref at call time, so `run` never closes over a stale value and
    // stays referentially stable across renders.
  }, []);

  // The effect depends on a stable key, not the `ctx` object identity, so it
  // fires only when the context genuinely changes (or readiness flips). The
  // `isReady` marker is folded into the key so gating open/close re-runs `run`.
  const isReadyMarker = opts?.isReady ? String(opts.isReady(ctx)) : 'ready';
  const dependencyKey = `${isReadyMarker}::${deriveKey(ctx, opts?.cacheKey)}`;
  const debounceMs = opts?.debounceMs;
  useEffect(() => {
    // With `debounceMs`, wait out the quiet period before departing. Rapid key
    // changes each re-run this effect; the cleanup clears the previous pending
    // timer, so successive changes coalesce into one `run()` after the silence.
    if (debounceMs !== undefined && debounceMs > 0) {
      // Invalidate any in-flight request from the previous key immediately,
      // before waiting out the debounce. Otherwise a call already departed for
      // the old key could resolve during the quiet period and briefly show
      // stale data for the new key. Aborting and bumping the request id here
      // makes `isStale()` reject that late result; the fresh call departs only
      // after the silence.
      controllerRef.current?.abort();
      requestIdRef.current += 1;
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = undefined;
        run();
      }, debounceMs);
    } else {
      run();
    }
    return () => {
      if (debounceTimerRef.current !== undefined) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = undefined;
      }
    };
  }, [dependencyKey, debounceMs, run]);

  return state;
}
